import { ApplicationHelpType, ZoneContextType } from '../../../utilities/Constants';
import {
    ILikeCaseLargestSettlement,
    ILikeCaseStatisticsBase,
} from '../../../interfaces/IAssessment';

import Common from '../../../stores/Common';
import { Help } from '../../shared/Help';
import { Link } from 'react-router-dom';
import { LoaderAnimation } from '../../shared/Loader';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { Stack } from 'react-bootstrap';
import { useCaseFileContext } from '../../../contexts/useCaseFileContext';
import { useEffect } from 'react';
import { useGetCaseStatistics } from './data/useGetCaseStatistics';
import { useZoneContext } from '../../../contexts/useZoneContext';

const averageTimeHelpText = `Resolved cases in the reference case set must have the following data available in order to be included:

# days after Date Served

1) Date Served or Date Filed AND
2) Resolution Agreement Date or Date of Dismissal

# days prior to Trial Date

1)	Resolution Agreement Date or Date of Dismissal AND
2)	Trial Date

If the Trial Date is On or before the actual Resolution Date, for the purposes of this calculation, Checkmate considers the Resolution Date to be the Trial Date.
`;

const blankSettlementCasesNotIncludedHelpText =
    'Cases without a final settlement are not included.';

const sixtyEightPercentageHelpText =
    'Approximately sixty-eight percent of settlements for matching cases will fall between the range.';

export function CaseStatistics(props: { isReady: boolean; onNoDataFound: () => void }) {
    const caseFile = useCaseFileContext();
    const { data, isLoading, isError, status } = useGetCaseStatistics(
        props.isReady,
        caseFile.currentCase.guid
    );
    const zoneContext = useZoneContext();

    useEffect(() => {
        if ((status === 'success' && data?.likeCaseStatisticsBase.numberOfCases === 0) || isError) {
            props.onNoDataFound();
        }
    }, [isLoading, data, isError, status]);

    if (isLoading) {
        return <LoaderAnimation size="xs" variant="secondary" />;
    }

    if (data?.likeCaseStatisticsBase.numberOfCases === 0) {
        return null;
    }

    const criteriaLabel = [
        data?.likeCaseStatisticsBase.jurisdiction,
        data?.likeCaseStatisticsBase.plaintiffsFirm,
        data?.likeCaseStatisticsBase.plaintiffsFirm ===
        data?.likeCaseStatisticsBase.nationalPlaintiffsFirm
            ? ''
            : data?.likeCaseStatisticsBase.nationalPlaintiffsFirm,
        data?.likeCaseStatisticsBase.disease,
    ].filter((x) => x !== '');

    return (
        <Stack gap={1}>
            {data?.likeCaseStatisticsBase.numberOfCases === 0 ? (
                <div>No matching cases found</div>
            ) : (
                <>
                    <Stack direction="horizontal" gap={2}>
                        <div style={{ width: 250, whiteSpace: 'nowrap' }}>
                            <strong>Criteria</strong>
                        </div>
                        <div>{criteriaLabel.join(' | ')}</div>
                    </Stack>
                    <Stack direction="horizontal" gap={2}>
                        <div style={{ width: 250, whiteSpace: 'nowrap' }}>
                            <strong>Number of Cases</strong>
                        </div>
                        <div>{data?.likeCaseStatisticsBase.numberOfCases}</div>
                    </Stack>
                    <Stack direction="horizontal" gap={2}>
                        <div style={{ width: 250, whiteSpace: 'nowrap' }}>
                            <strong>% of Cases Dismissed</strong>
                        </div>
                        <div>
                            <PercentageDisplay
                                numerator={data?.likeCaseStatisticsBase.numberOfDismissedCases}
                                denominator={data?.likeCaseStatisticsBase.numberOfCases}
                            />
                        </div>
                    </Stack>
                    <Stack direction="horizontal" gap={2}>
                        <div style={{ width: 250, whiteSpace: 'nowrap' }}>
                            <strong>% of Cases Settled</strong>
                        </div>
                        <div>
                            <PercentageDisplay
                                numerator={data?.likeCaseStatisticsBase.numberOfSettledCases}
                                denominator={data?.likeCaseStatisticsBase.numberOfCases}
                            />
                        </div>
                    </Stack>
                    <Stack direction="horizontal" gap={2}>
                        <div style={{ width: 250, whiteSpace: 'nowrap' }}>
                            <strong>Average Settlement</strong>
                            <Help
                                helpText={blankSettlementCasesNotIncludedHelpText}
                                title="Average Settlement"
                                type={ApplicationHelpType.Info}
                            ></Help>
                        </div>
                        <div>
                            {!data?.likeCaseStatisticsBase.settlementAverage
                                ? null
                                : Common.formatCurrency(
                                      data?.likeCaseStatisticsBase.settlementAverage!.toString()
                                  )}
                        </div>
                    </Stack>
                    <Stack direction="horizontal" gap={2}>
                        <div style={{ width: 250, whiteSpace: 'nowrap' }}>
                            <strong>Range for 68% of all Settlements</strong>
                            <Help
                                helpText={
                                    sixtyEightPercentageHelpText +
                                    ' ' +
                                    blankSettlementCasesNotIncludedHelpText
                                }
                                title="Average Settlement"
                                type={ApplicationHelpType.Info}
                            ></Help>
                        </div>
                        <div>
                            <SettlementRangeDisplay
                                likeCaseStatisticsBase={data?.likeCaseStatisticsBase}
                            />
                        </div>
                    </Stack>
                    <Stack direction="horizontal" gap={2} style={{ alignItems: 'start' }}>
                        <div style={{ width: 250, whiteSpace: 'nowrap' }}>
                            <strong>Largest Settlement</strong>
                        </div>
                        <div>
                            {!data?.likeCaseStatisticsBase.largestSettlement ? null : (
                                <LargestSettlementDisplay
                                    largestSettlement={
                                        data?.likeCaseStatisticsBase.largestSettlement ?? 0
                                    }
                                    likeCaseLargestSettlement={data?.likeCaseLargestSettlement}
                                    zoneContext={zoneContext}
                                />
                            )}
                        </div>
                    </Stack>
                    <Stack direction="horizontal" gap={2}>
                        <div style={{ width: 250, whiteSpace: 'nowrap' }}>
                            <strong>Average Timing for Dismissal</strong>{' '}
                            <Help
                                helpText={averageTimeHelpText}
                                title="Average Timing for Dismissal"
                                type={ApplicationHelpType.Info}
                            ></Help>
                        </div>
                        <div>
                            <AverageTimingDisplay
                                daysAfterFiled={
                                    data?.likeCaseStatisticsBase.averageDaysDismissalAfterDateFiled
                                }
                                daysPriorToTrial={
                                    data?.likeCaseStatisticsBase.averageDaysDismissalPriorTrialDate
                                }
                            />
                        </div>
                    </Stack>
                    <Stack direction="horizontal" gap={2}>
                        <div style={{ width: 250, whiteSpace: 'nowrap' }}>
                            <strong>Average Timing for Settlement</strong>
                            <Help
                                helpText={averageTimeHelpText}
                                title="Average Timing for Settlement"
                                type={ApplicationHelpType.Info}
                            ></Help>
                        </div>
                        <div>
                            <AverageTimingDisplay
                                daysAfterFiled={
                                    data?.likeCaseStatisticsBase.averageDaysSettlementAfterDateFiled
                                }
                                daysPriorToTrial={
                                    data?.likeCaseStatisticsBase.averageDaysSettlementPriorTrialDate
                                }
                            />
                        </div>
                    </Stack>
                </>
            )}
        </Stack>
    );
}

function PercentageDisplay(props: {
    numerator: number | undefined;
    denominator: number | undefined;
}) {
    if (props.denominator === undefined || props.numerator === undefined) {
        return null;
    }
    const value = (props.numerator / props.denominator!) * 100;

    return <>{value.toFixed(0)}%</>;
}

function LargestSettlementDisplay(props: {
    largestSettlement: number;
    likeCaseLargestSettlement?: ILikeCaseLargestSettlement[] | undefined;
    zoneContext: ZoneContextType;
}) {
    if (props.largestSettlement === 0) {
        return null;
    }

    const amount = Common.formatCurrency(props.largestSettlement.toString()) ?? '';

    const caseDisplays = props.likeCaseLargestSettlement?.map((x) => {
        const resDate = Common.safeDateFormat(x.resolutionDate);

        const caseLink = (
            <Link
                to={props.zoneContext.buildZoneUrl(
                    LocalRoutes.CaseFile.replace(':guid', x.caseGuid ?? '')
                )}
                target="_blank"
            >
                {x.caseName ?? ''}
            </Link>
        );

        const resDateDisplay = resDate ? `| ${resDate}` : '';
        const caseDisplay = (
            <span key={x.caseGuid}>
                {caseLink} {resDateDisplay} <br />
            </span>
        );

        return caseDisplay;
    });

    return (
        <>
            {amount}
            {(props.likeCaseLargestSettlement?.length ?? 0) > 1 ? <br /> : <> | </>}
            {caseDisplays}
        </>
    );
}

function AverageTimingDisplay(props: { daysAfterFiled?: number; daysPriorToTrial?: number }) {
    const labels = [];

    if (props.daysAfterFiled) {
        labels.push(`${Math.abs(Math.round(props.daysAfterFiled))} days after Date Served`);
    }

    if (props.daysPriorToTrial) {
        labels.push(`${Math.abs(Math.round(props.daysPriorToTrial))} days prior to Trial`);
    }

    return <>{labels.filter((x) => x.length > 0).join(' | ')}</>;
}

function SettlementRangeDisplay(props: {
    likeCaseStatisticsBase: ILikeCaseStatisticsBase | undefined;
}) {
    let from =
        (props.likeCaseStatisticsBase?.settlementAverage ?? 0) -
        (props.likeCaseStatisticsBase?.settlementStandardDeviation ?? 0);

    if (from < 0) {
        from = 0;
    }

    const to =
        (props.likeCaseStatisticsBase?.settlementAverage ?? 0) +
        (props.likeCaseStatisticsBase?.settlementStandardDeviation ?? 0);

    if (from === 0 && to === 0) {
        return null;
    }

    return (
        <>
            {Common.formatCurrency(from.toFixed(0))} to {Common.formatCurrency(to.toFixed(0))}
        </>
    );
}
